<template>
  <PageShell active="cart">
    <section class="cart-page">
      <h1>Your Cart Summary</h1>
      <div class="cart-table">
        <div class="cart-head">
          <label class="fake-check" :class="{ checked: allChecked }" @click="toggleAll"></label>
          <strong>Part Number</strong>
          <strong>Product Name</strong>
          <strong>Qty</strong>
          <strong>Selling Price</strong>
          <strong>Total price</strong>
          <i class="el-icon-delete" @click="clearCart"></i>
        </div>
        <div v-for="(row, index) in rows" :key="index" class="cart-row">
          <label class="fake-check" :class="{ checked: checkedItems[index] }" @click="toggleCheck(index)"></label>
          <span class="part-number">{{ row.partNumber }}</span>
          <span>{{ row.productName }}</span>
          <el-input v-model="row.qty" type="number" class="qty-input" @focus="onQtyFocus(row)"
            @change="onQtyChange(row)" />
          <span>{{ row.price }}</span>
          <span>{{ row.total }}</span>
          <button type="button" @click="removeRow(index)"><i class="el-icon-close"></i></button>
        </div>
      </div>
      <div class="cart-actions">
        <button type="button" @click="submitOrder">Submit order</button>
        <p>Your cart need(s) to be checked by us before your payment.<br>Click "Submit order" to start this process.</p>
      </div>
      <div class="cart-summary">
        <span>Selected items: {{ selectedRows.length }}</span>
        <span>Total amount: {{ totalAmount }}</span>
      </div>
    </section>
  </PageShell>
</template>

<script>
import PageShell from '@/components/common/PageShell.vue'
import { getToken } from '@/util/token-util.js'

export default {
  name: 'CartPage',
  components: {
    PageShell
  },
  data() {
    return {
      rows: [],
      checkedItems: [],
      loading: false
    }
  },
  computed: {
    allChecked() {
      return this.checkedItems.length > 0 && this.checkedItems.every(Boolean)
    },
    selectedRows() {
      return this.rows.filter((row, index) => this.checkedItems[index])
    },
    totalAmount() {
      const total = this.selectedRows.reduce((sum, row) => {
        const num = parseFloat(String(row.total).replace(/[^0-9.]/g, ''))
        return sum + (isNaN(num) ? 0 : num)
      }, 0)
      return '$' + total.toFixed(2)
    }
  },
  async mounted() {
    if (!getToken()) {
      this.$message.warning('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    await this.loadCart()
  },
  methods: {
    async loadCart() {
      this.loading = true
      try {
        const res = await this.$api({ url: '/Cart/list', method: 'get' })
        if (res.code === 200) {
          const list = res.data.items || []
          this.rows = list.map(item => ({
            id: item.id,
            partId: item.part_id,
            partNumber: item.part.part_number,
            productName: item.part.name,
            qty: item.quantity,
            price: '$' + item.price,
            total: '$' + (Number(item.price) * Number(item.quantity)).toFixed(2),
            cartId: item.id,
            stock: item.stock
          }))
          this.checkedItems = this.rows.map(() => true)
        }
      } catch (e) {
        this.$message.error('Failed to load cart')
      } finally {
        this.loading = false
      }
    },
    toggleCheck(index) {
      this.$set(this.checkedItems, index, !this.checkedItems[index])
    },
    toggleAll() {
      const all = this.allChecked
      this.checkedItems = this.checkedItems.map(() => !all)
    },
    onQtyFocus(row) {
      row._prevQty = row.qty
    },
    async onQtyChange(row) {
      const newQty = Number(row.qty)
      if (!newQty || newQty < 1) {
        this.$message.warning('Quantity must be at least 1')
        row.qty = row._prevQty
        return
      }
      try {
        const res = await this.$api({
          url: 'Cart/updateQuantity',
          method: 'post',
          data: { cart_item_id: row.cartId, quantity: newQty }
        })
        if (res.code === 200) {
          this.$message.success('Quantity updated')
          await this.loadCart()
          const price = parseFloat(String(row.price).replace(/[^0-9.]/g, ''))
          row.total = '$' + (price * newQty).toFixed(2)
        } else {
          this.$message.error(res.message || 'Failed to update quantity')
          row.qty = row._prevQty
        }
      } catch (e) {
        this.$message.error('Failed to update quantity')
        row.qty = row._prevQty
      }
    },
    clearCart() {
      this.$confirm('Are you sure to clear your cart?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Cart/remove',
            method: 'post',
            data: { cart_item_ids: this.selectedRows.map(row => row.cartId) }
          })
          if (res.code === 200) {
            this.$message.success('Cart cleared')
            await this.loadCart()
            this.$root.$emit('cart-updated')
          } else {
            this.$message.error(res.message || 'Failed to clear cart')
          }
        } catch (e) {
          this.$message.error('Failed to clear cart')
        }
      }).catch(() => { })
    },
    removeRow(index) {
      const row = this.rows[index]
      this.$confirm('Are you sure to remove this item from cart?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Cart/remove',
            method: 'post',
            data: { cart_item_ids: row.cartId }
          })
          if (res.code === 200) {
            this.$message.success('Item removed')
            await this.loadCart()
            this.$root.$emit('cart-updated')
          } else {
            this.$message.error(res.message || 'Failed to remove item')
          }
        } catch (e) {
          this.$message.error('Failed to remove item')
        }
      }).catch(() => { })
    },
    submitOrder() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('Please select at least one item')
        return
      }
      const selectedItems = this.selectedRows.map(row => ({
        id: row.id,
        part_id: row.partId,
        num: Number(row.qty)
      }))
      this.$router.push({
        name: 'checkout',
        query: { items: JSON.stringify(selectedItems) }
      })
    }
  }
}
</script>

<style scoped>
.cart-page {
  max-width: 1100px;
  min-height: 690px;
  margin: 0 auto;
  padding: 38px 20px 70px;
}

h1 {
  margin: 0 0 44px;
  color: #333;
  font-size: 32px;
}

.cart-table {
  width: 100%;
}

.cart-head,
.cart-row {
  display: grid;
  grid-template-columns: 56px 150px 1fr 200px 200px 200px 42px;
  align-items: center;
  min-height: 58px;
  padding: 0 26px;
}

.cart-head {
  background: #f3f3f3;
  color: #333;
  font-size: 16px;
}

.cart-head i {
  color: #999;
  font-size: 24px;
  cursor: pointer;
}

.cart-head i:hover {
  color: #F34B01;
}

.cart-row {
  border-bottom: 1px solid #d5d5d5;
  color: #333;
  font-size: 14px;
}

.fake-check {
  width: 24px;
  height: 24px;
  background: url(../../assets/check0.png) no-repeat center/contain;
  cursor: pointer;
}

.fake-check.checked {
  background-image: url(../../assets/check1.png);
}

.el-icon-delete {
  cursor: pointer;
}

.part-number {
  color: #F34B01;
}

.cart-row .qty-input {
  width: 100px;
}

.cart-row .qty-input ::v-deep .el-input__inner {
  height: 32px;
  padding-left: 14px;
  font-size: 15px;
}

.qty-input ::v-deep .el-input__inner:focus {
  border: 1px solid #F34B01;
  border-color: #F34B01;
}

.cart-row button {
  border: 0;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 24px;
}

.cart-actions {
  margin-top: 34px;
  display: flex;
  align-items: center;
  gap: 28px;
}

.cart-actions button {
  width: 140px;
  height: 44px;
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}

.cart-actions p {
  color: #333;
  font-size: 16px;
  line-height: 1.5;
}

.cart-summary {
  margin-top: 14px;
}

@media (max-width: 900px) {
  .cart-table {
    overflow-x: auto;
  }

  .cart-head,
  .cart-row {
    min-width: 900px;
  }
}
</style>
